import type { TranslationFunctions } from "@/i18n/i18n-types";
import { MemberRecipient, Student, StudentContact, studentSchemas } from "@/models";
import z from "zod";

const NotificationMediumValues = ['Email', 'SMS Text', 'Push'] as const
const NotificationStyleValues = ['Individual', 'Digest'] as const

const NotificationMediumSchema = z.enum(NotificationMediumValues)
const NotificationStyleSchema = z.enum(NotificationStyleValues)

export type NotificationMedium = z.infer<typeof NotificationMediumSchema>
export type NotificationStyle = z.infer<typeof NotificationStyleSchema>

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace NotificationMedium {
  export function Values() {
    return NotificationMediumValues
  }

  export function Icon(medium: NotificationMedium) {
    switch (medium) {
        case 'Email':
          return 'fa-envelope'
        case 'SMS Text':
          return 'fa-mobile'
        case 'Push':
          return 'fa-mobile' // TODO
      }
  }

  export function Translate(medium: NotificationMedium, translation: TranslationFunctions){
    switch (medium) {
      case 'Email':
        return translation.EMAIL()
      case 'SMS Text':
        return translation.SMS_TEXT()
      case 'Push':
        return translation.PUSH_NOTIFICATION()
    }
  }
}

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace NotificationStyle {
  export function Values() {
    return NotificationStyleValues
  }

  export function Translate(style: NotificationStyle, translation: TranslationFunctions){
    switch (style) {
      case 'Individual':
        return translation.NOTIFICATION_STYLE_INDIVIDUAL()
      case 'Digest':
        return translation.NOTIFICATION_STYLE_DIGEST()
    }
  }
}

const MemberPostNotificationPreferenceSchema = z.object({
  postPreferenceId: z.number(),
  memberId: z.number(),
  schoolId: z.number(),
  emailStyle: NotificationStyleSchema,
  emailEnabled: z.boolean(),
  textStyle: NotificationStyleSchema,
  textEnabled: z.boolean(),
  pushStyle: NotificationStyleSchema,
  pushEnabled: z.boolean(),
})

export class MemberPostNotificationPreference {
  constructor(data: z.infer<typeof MemberPostNotificationPreferenceSchema>) {
    Object.assign(this, data)
  }

  public readonly postPreferenceId!: number
  public readonly memberId!: number
  public readonly schoolId!: number
  public emailStyle!: NotificationStyle
  public emailEnabled!: boolean
  public textStyle!: NotificationStyle
  public textEnabled!: boolean
  public pushStyle!: NotificationStyle
  public pushEnabled!: boolean

  getElementId<T extends keyof MemberPostNotificationPreference>(prop: T) {
    return `member-${this.memberId}-post-notification-preference-${this.postPreferenceId}-${prop}`
  }
}

const MemberConversationNotificationPreferenceSchema = z.object({
  conversationPreferenceId: z.number(),
  memberId: z.number(),
  medium: NotificationMediumSchema,
  enabled: z.boolean(),
})

export class MemberConversationNotificationPreference {
  constructor(data: z.infer<typeof MemberConversationNotificationPreferenceSchema>) {
    Object.assign(this, data)
  }

  conversationPreferenceId!: number
  memberId!: number
  medium!: NotificationMedium
  enabled!: boolean

  public getElementId<T extends keyof MemberConversationNotificationPreference>(prop: T) {
    return `member-${this.memberId}-conversation-notification-preference-${this.conversationPreferenceId}-${prop}`
  }
}

const MobileAppRegistrationSchema = z.object({
  deviceId: z.string().nonempty(),
  memberId: z.number().gt(0),
  deviceName: z.string(),
  registered: z.coerce.date(),
  lastAccessed: z.coerce.date(),
})

export class MobileAppRegistration {
  constructor(data: z.infer<typeof MobileAppRegistrationSchema>) {
    Object.assign(this, data)
  }

  public readonly deviceId!: string
  public readonly memberId!: number
  public readonly deviceName!: string
  public readonly registered!: Date
  public readonly lastAccessed!: Date
}

const MemberProfileSchema = z.object({
  memberId: z.number().gt(0),
  firstName: z.string().nonempty(),
  lastName: z.string().nonempty(),
  profilePhotoId: z.string().uuid().optional(),
  email: z.string().optional(),
  phoneNumber: z.string().optional(),
  language: z.string().optional(),
  hasMobileApp: z.boolean(),
  students: z.array(studentSchemas.Student),
  contacts: z.array(studentSchemas.StudentContact),
  postNotificationPreferences: z.array(MemberPostNotificationPreferenceSchema),
  conversationNotificationPreferences: z.array(MemberConversationNotificationPreferenceSchema)
})

export class MemberProfile {
  constructor(data: z.infer<typeof MemberProfileSchema>) {
    Object.assign(this, data)
    this.students = data.students.map(v => new Student(v))
    this.contacts = data.contacts.map(v => new StudentContact(v))
    this.postNotificationPreferences = data.postNotificationPreferences.map(v => new MemberPostNotificationPreference(v))
    this.conversationNotificationPreferences = data.conversationNotificationPreferences.map(v => new MemberConversationNotificationPreference(v))
  }

  public readonly memberId!: number
  public readonly firstName!: string
  public readonly lastName!: string
  public profilePhotoId?: string
  public readonly email?: string
  public readonly phoneNumber?: string
  public readonly language?: string
  public readonly hasMobileApp!: boolean
  public readonly students: Student[]
  public readonly contacts: StudentContact[]
  public readonly postNotificationPreferences: MemberPostNotificationPreference[]
  public readonly conversationNotificationPreferences: MemberConversationNotificationPreference[]

  fullName() {
    return `${this.firstName} ${this.lastName}`
  }

  toRecipient() {
    return new MemberRecipient({
      $type: "Member",
      ... this
    })
  }
}

const MemberOverviewSchema = MemberProfileSchema.pick({
  memberId: true,
  firstName: true,
  lastName: true,
  profilePhotoId: true,
})
export class MemberOverview {
  constructor(data: z.infer<typeof MemberOverviewSchema>) {
    Object.assign(this, data)
  }

  public readonly memberId!: number
  public readonly firstName!: string
  public readonly lastName!: string
  public readonly profilePhotoId!: string

  get fullName() {
    return `${this.firstName} ${this.lastName}`
  }
}

export const memberSchemas = {
  MemberConversationNotificationPreference: MemberConversationNotificationPreferenceSchema,
  MemberPostNotificationPreference: MemberPostNotificationPreferenceSchema,
  MemberProfile: MemberProfileSchema,
  NotificationMedium: NotificationMediumSchema,
  NotificationStyle: NotificationStyleSchema,
  MemberOverview: MemberOverviewSchema,
  MobileAppRegistration: MobileAppRegistrationSchema
}
